// MergeReviewWizard
//
// The unified contact-merge UX that supersedes both ContactMergeWizard
// (per-field 2-way) and ClusterMergeModal (blind cluster merge,
// no per-field control). Renders for any 2-contact merge surface;
// the parent decides which two contacts to pass in.
//
// Headline UX additions over the predecessors:
//   * All 29 mergeable fields visible by default (conflicts highlighted,
//     non-conflicts collapsed under "Show identical fields" toggle).
//   * Per-field star UI replaces the radio-pair. Click a star to lock
//     a specific value as the winner.
//   * Email + phone_primary support "Keep both" — promotes the loser
//     into the survivor's secondary_email / phone_secondary slot
//     instead of dropping it. This is what migration 0057's
//     `secondary_email` column unlocks.
//   * Cross-household banner — when the two contacts live in different
//     households, surfaces a routing nudge to merge HOUSEHOLDS first
//     (since opportunities + transactions + service records are
//     household-scoped, not contact-scoped).
//   * Sensitive-field warning panel from PR #292 re-used verbatim.
//
// Out of scope for this PR (deferred to PR 4):
//   * Wiring this into the cluster-merge page, the spouse-pass flow,
//     bulk-merge modal, household-merge picker.
//   * Deletion of ContactMergeWizard + ClusterMergeModal.
//
// State is intentionally local — the wizard is a controlled overlay.
// Parent passes `survivor` + `merged` (already resolved SafeContacts),
// `onMerged(survivorId)` for refresh routing, and `onCancel`.

import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Circle,
  GitMerge,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { api, ApiError, type SafeContact } from "../api";
import { Button, Card, ErrorBanner, FormField, Pill, Row } from "../ui";
import { DestructiveActionConfirm } from "./DestructiveActionConfirm";
import { useToast } from "./Toast";
import {
  KEEP_BOTH_FIELDS,
  MERGEABLE_FIELDS_UI,
  buildDefaultChoices,
  composeMergeRequest,
  countPopulatedFields,
  detectConflicts,
  detectCrossHousehold,
  isBlank,
  type PerFieldChoice,
  type PerFieldChoiceMap,
} from "./merge-review-helpers";

// Sensitive-field warning panel — copied verbatim from
// ClusterMergeModal (PR #292 / Wave 2.4) so the merge surface keeps
// gating on data-loss acknowledgement. Encrypted SSN / DL / bank
// values on the merged contact are NOT carried across by the
// backend; this gate makes that loss explicit.
const SENSITIVE_FIELDS_TO_WATCH: Array<{
  flag: keyof Pick<
    SafeContact,
    | "has_ssn_on_file"
    | "has_spouse_ssn_on_file"
    | "has_coborrower_ssn_on_file"
    | "has_drivers_license_on_file"
    | "has_bank_account_on_file"
    | "has_bank_routing_on_file"
  >;
  label: string;
}> = [
  { flag: "has_ssn_on_file", label: "SSN" },
  { flag: "has_spouse_ssn_on_file", label: "Spouse SSN" },
  { flag: "has_coborrower_ssn_on_file", label: "Co-borrower SSN" },
  { flag: "has_drivers_license_on_file", label: "Driver's License" },
  { flag: "has_bank_account_on_file", label: "Bank account number" },
  { flag: "has_bank_routing_on_file", label: "Bank routing number" },
];

/**
 * Per-contact household + freshness context surfaced as a one-row strip
 * above each SurvivorCard. Optional — when the wizard is invoked from
 * a surface that doesn't know the household details (e.g. the find-
 * duplicates modal), the prop is omitted and the strip is hidden.
 */
export interface MergeContactHouseholdContext {
  household_display_name: string | null;
  primary_city: string | null;
  primary_state: string | null;
  primary_zip: string | null;
  member_count: number | null;
}

export interface MergeReviewWizardProps {
  survivor: SafeContact;
  merged: SafeContact;
  /**
   * Optional pre-filled reason — useful when the parent knows the
   * merge context (e.g. "Cluster merge from duplicates queue").
   */
  initialReason?: string;
  /**
   * Per-contact household summary for the SurvivorCard strip. Both
   * are optional and rendered independently (one side may know its
   * household while the other doesn't, e.g. recently-imported lead).
   */
  survivorHouseholdContext?: MergeContactHouseholdContext;
  mergedHouseholdContext?: MergeContactHouseholdContext;
  onMerged: (survivorId: string) => void;
  onCancel: () => void;
}

export function MergeReviewWizard({
  survivor,
  merged,
  initialReason,
  survivorHouseholdContext,
  mergedHouseholdContext,
  onMerged,
  onCancel,
}: MergeReviewWizardProps) {
  const toast = useToast();

  // Per-field choice state. Initialised once from buildDefaultChoices
  // and then carried across re-renders. Re-derived only if the
  // identity of survivor/merged changes (parent swap).
  const [choices, setChoices] = useState<PerFieldChoiceMap>(() =>
    buildDefaultChoices(survivor, merged),
  );

  useEffect(() => {
    setChoices(buildDefaultChoices(survivor, merged));
    // Intentionally narrow deps — only re-init when the parent swaps
    // the contacts being merged. Inner choice updates are not in the
    // deps because we don't want to clobber user picks.
  }, [survivor.id, merged.id, survivor, merged]);

  // Wave 1.4 (May 2026 — data-hygiene merge UX follow-up):
  // default to ALL fields visible. Previous default was "conflicts
  // only," which made the wizard feel like a yes/no merge affirmation
  // when the two contacts had only a couple of differing fields —
  // users couldn't see the bulk of mergeable fields and assumed they
  // were merging the whole record. Showing every field by default
  // makes the per-field choice surface obvious; the toggle still
  // collapses to conflicts-only when the user wants the compact view.
  const [showAllFields, setShowAllFields] = useState(true);
  const [reason, setReason] = useState(initialReason ?? "");
  const [reasonCode, setReasonCode] = useState<string>("");
  const [reasonCodeOptions, setReasonCodeOptions] = useState<
    Array<{
      code: string;
      display_label: string;
      description: string;
      requires_text: boolean;
    }> | null
  >(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [ackedSensitiveLoss, setAckedSensitiveLoss] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Step 3 (Baker merge feedback) — "While you're here" enrichment: set
  // a value the merge can't (neither record has it). Curated to the two
  // fields Baker named — a go-by/preferred name and marital status — so
  // the merge stays a merge, not an everything-edit-form. Applied as an
  // audited updateContact on the survivor AFTER the merge succeeds; a
  // failure there never fails the merge.
  const [goBy, setGoBy] = useState(survivor.preferred_name ?? "");
  const [maritalStatus, setMaritalStatus] = useState(
    survivor.marital_status ?? "",
  );
  useEffect(() => {
    setGoBy(survivor.preferred_name ?? "");
    setMaritalStatus(survivor.marital_status ?? "");
  }, [survivor.id, survivor.preferred_name, survivor.marital_status]);

  // Phase D2: load the reason-code catalog (filtered to action="merge")
  // when the wizard mounts. Errors are non-blocking — the user falls
  // back to free-text only.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await api.listChangeReasonCodes("merge");
        if (!cancelled) {
          setReasonCodeOptions(
            result.codes.map((c) => ({
              code: c.code,
              display_label: c.display_label,
              description: c.description,
              requires_text: c.requires_text,
            })),
          );
        }
      } catch {
        // Silent — the wizard still works with free-text-only reason.
        if (!cancelled) setReasonCodeOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedReasonCodeRow =
    reasonCodeOptions?.find((c) => c.code === reasonCode) ?? null;
  const reasonTextRequired =
    selectedReasonCodeRow === null
      ? true /* free-text-only fallback path requires text */
      : selectedReasonCodeRow.requires_text;

  const conflicts = useMemo(
    () => new Set(detectConflicts(survivor, merged)),
    [survivor, merged],
  );

  const crossHousehold = useMemo(
    () => detectCrossHousehold([survivor, merged]),
    [survivor, merged],
  );

  // Sensitive-data loss only triggers when the MERGED contact has a
  // sensitive value the SURVIVOR doesn't. Survivor's own sensitive
  // values are kept regardless of the merge.
  //
  // Baker bug 2026-05-28: previously this only checked the merged
  // contact's flag, so the warning fired even when the survivor
  // ALREADY had the same sensitive field on file (e.g. two duplicate
  // contacts where both have SSN — no data is actually being lost).
  // That silently disabled the Merge button and Baker spent hours
  // trying different reason codes thinking the dropdown was the
  // problem. New logic: warn only on actual loss (merged has it,
  // survivor doesn't), so the gate fires only when the operator
  // genuinely needs to acknowledge real data loss.
  const sensitiveLossLabels = useMemo(() => {
    const labels: string[] = [];
    for (const sf of SENSITIVE_FIELDS_TO_WATCH) {
      if (merged[sf.flag] && !survivor[sf.flag]) {
        labels.push(sf.label);
      }
    }
    return labels;
  }, [merged, survivor]);

  const survivorPopulated = countPopulatedFields(survivor);
  const mergedPopulated = countPopulatedFields(merged);

  // Marital-status options for the enrichment dropdown. The field is
  // free-text elsewhere, so keep any existing non-standard value
  // selectable (never silently drop it).
  const STD_MARITAL = ["Single", "Married", "Divorced", "Widowed", "Separated"];
  const current = survivor.marital_status ?? "";
  const maritalOptions =
    current !== "" && !STD_MARITAL.includes(current)
      ? [current, ...STD_MARITAL]
      : STD_MARITAL;

  // Phase 11.x (May 2026): hide rows where neither side has a value.
  // The user shouldn't have to scroll past a wall of "— / —" rows
  // for fields like Suffix / Apt / Marital status when neither
  // contact has data on either side. Filter applies to BOTH the
  // all-fields view AND the conflicts-only view (conflicts already
  // imply at least one side has data, so the filter is a no-op
  // there — kept uniform for clarity).
  const populatedFields = useMemo(() => {
    const set = new Set<string>();
    for (const f of MERGEABLE_FIELDS_UI) {
      const sv = (survivor as unknown as Record<string, unknown>)[f.key];
      const mv = (merged as unknown as Record<string, unknown>)[f.key];
      if (!isBlank(sv) || !isBlank(mv)) set.add(f.key);
    }
    return set;
  }, [survivor, merged]);

  const fieldsToRender = useMemo(() => {
    const base = showAllFields
      ? MERGEABLE_FIELDS_UI
      : MERGEABLE_FIELDS_UI.filter((f) => conflicts.has(f.key));
    return base.filter((f) => populatedFields.has(f.key));
  }, [showAllFields, conflicts, populatedFields]);

  // Identical-but-populated fields = populated fields that aren't
  // conflicts. The toggle hides these (or shows them — depending on
  // showAllFields). Empty-everywhere fields are NEVER counted here
  // because they're hidden in both modes.
  const hiddenFieldCount = Math.max(
    0,
    populatedFields.size - conflicts.size,
  );

  function setChoice(field: string, choice: PerFieldChoice) {
    setChoices((prev) => ({ ...prev, [field]: choice }));
  }

  async function submit() {
    // Phase D2: a reason code is the audit-log primary key for merges.
    // Free-text is required only when the chosen code's
    // requires_text=true OR when the catalog didn't load (fallback).
    if (reasonCodeOptions !== null && reasonCodeOptions.length > 0 && reasonCode === "") {
      setSubmitError("Pick a reason code.");
      return;
    }
    if (reasonTextRequired && reason.trim() === "") {
      setSubmitError(
        selectedReasonCodeRow?.requires_text
          ? "This reason code requires a free-text description."
          : "Reason is required.",
      );
      return;
    }
    if (sensitiveLossLabels.length > 0 && !ackedSensitiveLoss) {
      setSubmitError(
        "Acknowledge the sensitive-data loss warning before merging.",
      );
      return;
    }
    // 2026-05-24 (audit-sweep PR E): two-phase confirm so the
    // DestructiveActionConfirm modal can sit between the user clicking
    // Merge and the actual API call. submit() validates + opens the
    // confirm; executeSubmit() (below) runs the merge after the user
    // confirms. Replaces a window.confirm pop-up.
    setSubmitError(null);
    setConfirmOpen(true);
  }

  async function executeSubmit() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const composed = composeMergeRequest(choices);
      // Build the canonical free-text reason: caller's text if
      // present, otherwise fall back to the picked code's display
      // label so the audit log always has something humans can read.
      const fallbackText =
        reason.trim() !== ""
          ? reason.trim()
          : (selectedReasonCodeRow?.display_label ?? "Merge");
      const result = await api.mergeContacts(survivor.id, merged.id, {
        field_overrides: composed.field_overrides as Record<
          string,
          "primary" | "merged"
        >,
        secondary_promotions: composed.secondary_promotions,
        reason: fallbackText,
        ...(reasonCode !== "" ? { reason_code: reasonCode } : {}),
      });
      const fieldCount = result.applied_field_overrides.length;
      const skippedCount = result.skipped_blank_overrides?.length ?? 0;
      const refsCount = Object.values(result.counts).reduce(
        (a, b) => a + b,
        0,
      );
      const skippedSuffix =
        skippedCount > 0
          ? ` Protected ${skippedCount} populated field${skippedCount === 1 ? "" : "s"} from being blanked: ${result.skipped_blank_overrides
              .map((f) => f.field_name)
              .join(", ")}.`
          : "";
      toast.success(
        `Merged. ${fieldCount} field${fieldCount === 1 ? "" : "s"} updated; ${refsCount} reference${refsCount === 1 ? "" : "s"} re-parented.${skippedSuffix}`,
      );

      // Step 3 — apply any "While you're here" enrichment to the
      // survivor (a value the merge couldn't set). Only the changed
      // fields; a failure here is non-fatal (the merge already
      // succeeded) — surface a warning toast and move on.
      const enrich: Record<string, string | null> = {};
      const goByTrim = goBy.trim();
      if (goByTrim !== (survivor.preferred_name ?? "")) {
        enrich.preferred_name = goByTrim === "" ? null : goByTrim;
      }
      if (maritalStatus !== (survivor.marital_status ?? "")) {
        enrich.marital_status = maritalStatus === "" ? null : maritalStatus;
      }
      if (Object.keys(enrich).length > 0) {
        try {
          await api.updateContact(result.primary_contact.id, enrich);
        } catch {
          toast.error(
            "Merged, but the extra details (go-by / marital status) didn't save — set them on the contact page.",
          );
        }
      }

      setConfirmOpen(false);
      onMerged(result.primary_contact.id);
    } catch (err) {
      // Brian Baker bug 2026-05-26: merge attempts that fail before
      // the audit_log entry (e.g., requireSystemAdmin rejection,
      // reason validation, rate limit, mergeContacts() returning
      // not_found) left NO trail. The submitError banner is set, but
      // if the user dismisses the modal or the network bounces, we
      // lose the failure mode. Log to console with the API endpoint
      // + ids so DevTools shows what hit the wire for next time.
      console.error("[cleanup] mergeContacts request failed", {
        survivor_id: survivor.id,
        merged_id: merged.id,
        endpoint: `/api/contacts/${encodeURIComponent(survivor.id)}/merge-from/${encodeURIComponent(merged.id)}`,
        error: err,
      });
      if (err instanceof ApiError) {
        setSubmitError(`${err.status} — ${err.message}`);
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError("Unknown error during merge");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card className="merge-review-wizard">
      <header className="merge-review-wizard-header">
        <Row gap={12} align="center">
          <GitMerge size={22} strokeWidth={1.5} />
          <h2 style={{ margin: 0 }}>Review &amp; merge contacts</h2>
        </Row>
        <p className="muted" style={{ margin: "6px 0 0", fontSize: 14 }}>
          Pick which fields the surviving contact keeps. Conflicts are
          shown by default; non-conflicting fields are hidden behind
          the toggle below.
        </p>
      </header>

      {!crossHousehold.sameHousehold && (
        <CrossHouseholdBanner
          householdIds={crossHousehold.householdIds}
          hasOrphan={crossHousehold.hasOrphan}
        />
      )}

      {submitError && <ErrorBanner>{submitError}</ErrorBanner>}

      <section className="merge-review-step">
        <h3 className="merge-review-step-title">Survivor</h3>
        <Row gap={12} align="start" wrap>
          <SurvivorCard
            contact={survivor}
            isSurvivor={true}
            populated={survivorPopulated.populated}
            total={survivorPopulated.total}
            householdContext={survivorHouseholdContext}
          />
          <span className="merge-review-arrow" aria-hidden="true">
            <ArrowLeft size={16} strokeWidth={1.5} aria-hidden />
          </span>
          <SurvivorCard
            contact={merged}
            isSurvivor={false}
            populated={mergedPopulated.populated}
            total={mergedPopulated.total}
            householdContext={mergedHouseholdContext}
          />
        </Row>
      </section>

      <section className="merge-review-step">
        <Row gap={12} align="baseline" justify="space-between">
          <h3 className="merge-review-step-title">
            Field review · {conflicts.size} conflict
            {conflicts.size === 1 ? "" : "s"}
          </h3>
          {hiddenFieldCount > 0 && (
            <button
              type="button"
              className="merge-review-toggle"
              onClick={() => setShowAllFields((v) => !v)}
            >
              {showAllFields
                ? `Hide ${hiddenFieldCount} identical field${hiddenFieldCount === 1 ? "" : "s"}`
                : `Show all ${populatedFields.size} populated field${populatedFields.size === 1 ? "" : "s"}`}
            </button>
          )}
        </Row>

        {fieldsToRender.length === 0 ? (
          <p className="muted" style={{ fontSize: 14 }}>
            No conflicts — every field is identical between the two
            contacts. The merge will preserve the survivor's values
            and re-point all references.
          </p>
        ) : (
          <div className="merge-review-table">
            <div className="merge-review-row merge-review-row--head">
              <div>Field</div>
              <div>Survivor</div>
              <div>Merged</div>
              <div>Winner</div>
            </div>
            {fieldsToRender.map((f) => {
              const survivorValue = (
                survivor as unknown as Record<string, unknown>
              )[f.key];
              const mergedValue = (
                merged as unknown as Record<string, unknown>
              )[f.key];
              const isConflict = conflicts.has(f.key);
              const choice = choices[f.key] ?? "primary";
              return (
                <MergeReviewFieldRow
                  key={f.key}
                  fieldKey={f.key}
                  label={f.label}
                  survivorValue={survivorValue}
                  mergedValue={mergedValue}
                  isConflict={isConflict}
                  choice={choice}
                  onChoose={(c) => setChoice(f.key, c)}
                />
              );
            })}
          </div>
        )}
      </section>

      {sensitiveLossLabels.length > 0 && (
        <SensitiveLossPanel
          labels={sensitiveLossLabels}
          acked={ackedSensitiveLoss}
          onAck={setAckedSensitiveLoss}
        />
      )}

      {/* Step 3 — capture something you know that neither record has
          (Baker: "while we're merging, hey, this is what she goes by",
          and "I know she's married"). Optional; applied to the survivor
          after the merge. Curated to two fields so merge stays a merge. */}
      <section className="merge-review-step">
        <h3 className="merge-review-step-title">While you're here (optional)</h3>
        <p className="muted" style={{ fontSize: 14, margin: "0 0 10px" }}>
          Know something neither record captured? Set it on the surviving
          contact — saved with the merge.
        </p>
        <Row gap={12} wrap>
          <div style={{ flex: "1 1 240px" }}>
            <FormField
              label="Goes by (preferred name)"
              name="merge_go_by"
              value={goBy}
              hint='e.g. "Allie" for Alyssa. Leave as-is to keep the current value.'
              onChange={(e) => setGoBy(e.target.value)}
            />
          </div>
          <div style={{ flex: "1 1 240px" }}>
            <label
              htmlFor="merge_marital_status"
              style={{
                display: "block",
                fontSize: 13,
                marginBottom: 4,
                fontWeight: 600,
              }}
            >
              Marital status
            </label>
            <select
              id="merge_marital_status"
              value={maritalStatus}
              onChange={(e) => setMaritalStatus(e.target.value)}
              disabled={submitting}
              style={{
                width: "100%",
                padding: "6px 8px",
                border: "1px solid var(--mid-gray)",
                borderRadius: "var(--radius-compact)",
                fontSize: 13,
                fontFamily: "inherit",
                background: "var(--bg-0)",
              }}
            >
              <option value="">(not set)</option>
              {maritalOptions.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        </Row>
      </section>

      <section className="merge-review-step">
        {/* Phase D2: canonical reason-code dropdown. Filters to
            action="merge". When the catalog hasn't loaded (or returned
            empty), we still show the free-text field so the wizard
            falls back gracefully. */}
        {reasonCodeOptions !== null && reasonCodeOptions.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <label
              htmlFor="merge_reason_code"
              style={{
                display: "block",
                fontSize: 13,
                marginBottom: 4,
                fontWeight: 600,
              }}
            >
              Reason code (required, audit-logged)
            </label>
            <select
              id="merge_reason_code"
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              disabled={submitting}
              style={{
                width: "100%",
                padding: "6px 8px",
                border: "1px solid var(--mid-gray)",
                borderRadius: "var(--radius-compact)",
                fontSize: 13,
                fontFamily: "inherit",
                background: "var(--bg-0)",
              }}
            >
              <option value="" disabled>
                Pick a reason…
              </option>
              {reasonCodeOptions.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.display_label}
                </option>
              ))}
            </select>
            {selectedReasonCodeRow && (
              <p
                className="muted"
                style={{
                  margin: "4px 0 0",
                  fontSize: 12,
                  fontStyle: "italic",
                }}
              >
                {selectedReasonCodeRow.description}
              </p>
            )}
          </div>
        )}
        <FormField
          label={
            reasonTextRequired
              ? "Free-text reason (required, audit-logged)"
              : "Free-text reason (optional)"
          }
          name="merge_reason"
          {...(reasonTextRequired ? { required: true } : {})}
          value={reason}
          hint={
            reasonTextRequired
              ? "Examples: 'duplicate AC contacts #422 + #4321', 'spouse pass missed match'."
              : "Optional supplementary context. The reason code above is the primary audit dimension."
          }
          onChange={(e) => setReason(e.target.value)}
        />
      </section>

      <footer className="merge-review-footer">
        {/* Baker bug 2026-05-28 — explicit hint under the disabled
            Merge button so the operator knows WHY it's disabled.
            Previously the button just sat grayed out and Baker
            attributed the no-op to the reason picker. */}
        {sensitiveLossLabels.length > 0 && !ackedSensitiveLoss && !submitting && (
          <div
            role="status"
            style={{
              fontSize: 12,
              color: "var(--danger, #c0392b)",
              textAlign: "right",
              marginBottom: 6,
            }}
          >
            <AlertTriangle size={12} strokeWidth={1.5} aria-hidden /> Acknowledge
            the sensitive-data warning above to enable the Merge button.
          </div>
        )}
        <Row gap={8} justify="end">
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={
              submitting ||
              (sensitiveLossLabels.length > 0 && !ackedSensitiveLoss)
            }
          >
            {submitting ? "Merging…" : "Merge"}
          </Button>
        </Row>
      </footer>
      <DestructiveActionConfirm
        open={confirmOpen}
        actionName={`Merge "${displayName(merged)}" INTO "${displayName(survivor)}"`}
        consequences={[
          `The "${displayName(merged)}" contact is sidelined (soft-deleted with merged_into_contact_id pointer).`,
          `All of its activities, notes, tags, contact-roles, opportunities, transactions, rhythms, and service-record refs re-parent to "${displayName(survivor)}".`,
          `The per-field choices you made above are applied to the survivor.`,
          sensitiveLossLabels.length > 0
            ? `Sensitive fields you chose to drop will be cleared: ${sensitiveLossLabels.join(", ")}.`
            : `The merge is captured in the audit log with your reason code.`,
        ]}
        reversibilityNote="Reversible via /api/contacts/:id/unmerge — but unmerging requires admin access, so double-check the field choices and reason code first."
        confirmLabel="Merge contacts"
        confirmTone="danger"
        showReasonField={false}
        running={submitting}
        error={submitError}
        onCancel={() => {
          if (!submitting) setConfirmOpen(false);
        }}
        onConfirm={() => void executeSubmit()}
      />
    </Card>
  );
}

// ─── Sub-components ────────────────────────────────────────────────

function CrossHouseholdBanner({
  householdIds,
  hasOrphan,
}: {
  householdIds: string[];
  hasOrphan: boolean;
}) {
  return (
    <div className="merge-review-cross-household-banner" role="status">
      <strong>Different households detected.</strong>{" "}
      {hasOrphan
        ? "One contact has no household assigned."
        : `Contacts live in ${householdIds.length} different households.`}{" "}
      Merging contacts will move activities, tags, and notes to the
      survivor's household. <strong>Service records, opportunities,
      and transactions stay with their original households</strong> —
      they're household-scoped, not contact-scoped. To consolidate the
      whole person, run <em>Merge households…</em> first.
    </div>
  );
}

function SurvivorCard({
  contact,
  isSurvivor,
  populated,
  total,
  householdContext,
}: {
  contact: SafeContact;
  isSurvivor: boolean;
  populated: number;
  total: number;
  householdContext?: MergeContactHouseholdContext | undefined;
}) {
  return (
    <div
      className={`merge-review-survivor-card${isSurvivor ? " is-survivor" : ""}`}
    >
      <div className="merge-review-survivor-card-header">
        {isSurvivor ? (
          <Pill variant="state-active_client">Survivor</Pill>
        ) : (
          <Pill variant="state-past_client">Merged → sidelined</Pill>
        )}
      </div>
      <h4 className="merge-review-survivor-card-name">
        {displayName(contact)}
      </h4>
      {/* Step 3 (Baker: "I don't know who is who… which was before").
          Recency answers it: the OLDER record is the original identity,
          the recently-updated one is the live record. */}
      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
        {contact.created_at
          ? `In the system since ${monthYear(contact.created_at)}`
          : "Added date unknown"}
        {contact.updated_at
          ? ` · updated ${relativeFromNow(contact.updated_at)}`
          : ""}
      </div>
      {householdContext && (
        <HouseholdContextStrip
          householdId={contact.household_id}
          context={householdContext}
          updatedAt={contact.updated_at}
        />
      )}
      <div className="merge-review-survivor-card-meta">
        <span className="muted">
          {populated} of {total} fields populated
        </span>
        {!householdContext && contact.household_id && (
          <span className="muted"> · Household {contact.household_id.slice(0, 8)}</span>
        )}
      </div>
      <div className="merge-review-survivor-card-id muted">{contact.id}</div>
    </div>
  );
}

/**
 * One-row household + freshness strip rendered above each merge
 * comparison card. Helps the user disambiguate two records without
 * round-tripping through the household page: shows household name
 * (linked when known), member count, mailing-address city/state/zip,
 * and a relative "Updated N days ago" annotation.
 */
function HouseholdContextStrip({
  householdId,
  context,
  updatedAt,
}: {
  householdId: string | null;
  context: MergeContactHouseholdContext;
  updatedAt: string | null;
}) {
  const cityState = [context.primary_city, context.primary_state]
    .filter(Boolean)
    .join(", ");
  const addressLine = [cityState, context.primary_zip]
    .filter(Boolean)
    .join(" ")
    .trim();
  const memberCountLabel =
    context.member_count !== null && context.member_count > 0
      ? `${context.member_count} member${context.member_count === 1 ? "" : "s"}`
      : null;
  return (
    <div className="merge-review-household-strip">
      {context.household_display_name ? (
        householdId ? (
          <a
            className="merge-review-household-strip-link"
            href={`/households/${encodeURIComponent(householdId)}`}
            target="_blank"
            rel="noreferrer"
          >
            {context.household_display_name}
          </a>
        ) : (
          <strong>{context.household_display_name}</strong>
        )
      ) : (
        <span className="muted">No household</span>
      )}
      {memberCountLabel && <span className="muted"> · {memberCountLabel}</span>}
      {addressLine && <span className="muted"> · {addressLine}</span>}
      {updatedAt && (
        <span className="muted"> · Updated {relativeFromNow(updatedAt)}</span>
      )}
    </div>
  );
}

/**
 * Compact "3 days ago" / "today" / "2 weeks ago" formatter for the
 * household-context strip. Shared with the duplicate-queue card.
 */
// "Jun 2019" — absolute month/year so two records' ages are directly
// comparable at a glance (relative "5 years ago" is harder to compare).
export function monthYear(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

export function relativeFromNow(iso: string | null): string {
  if (!iso) return "—";
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return iso;
  const diffMs = Date.now() - ts;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return "today";
  if (diffDays === 1) return "yesterday";
  if (diffDays < 30) return `${diffDays} days ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) {
    return `${diffMonths} month${diffMonths === 1 ? "" : "s"} ago`;
  }
  const diffYears = Math.floor(diffMonths / 12);
  return `${diffYears} year${diffYears === 1 ? "" : "s"} ago`;
}

function MergeReviewFieldRow({
  fieldKey,
  label,
  survivorValue,
  mergedValue,
  isConflict,
  choice,
  onChoose,
}: {
  fieldKey: string;
  label: string;
  survivorValue: unknown;
  mergedValue: unknown;
  isConflict: boolean;
  choice: PerFieldChoice;
  onChoose: (choice: PerFieldChoice) => void;
}) {
  const supportsKeepBoth = KEEP_BOTH_FIELDS.has(fieldKey as never);
  return (
    <div
      className={`merge-review-row${isConflict ? " is-conflict" : ""}`}
      data-field={fieldKey}
    >
      <div className="merge-review-row-label">{label}</div>
      <ValueCell
        value={survivorValue}
        picked={choice === "primary" || choice === "keep_both"}
        onPick={() => onChoose("primary")}
        disabled={!isConflict}
        ariaLabel={`Keep survivor's ${label}`}
      />
      <ValueCell
        value={mergedValue}
        picked={choice === "merged" || choice === "keep_both"}
        onPick={() => onChoose("merged")}
        disabled={!isConflict}
        ariaLabel={`Use merged contact's ${label}`}
      />
      <div className="merge-review-row-action">
        {isConflict && supportsKeepBoth && (
          <button
            type="button"
            className={`merge-review-keep-both${choice === "keep_both" ? " is-active" : ""}`}
            onClick={() => onChoose("keep_both")}
            title={`Keep both — promote merged contact's ${label} to the survivor's secondary slot.`}
          >
            Keep both <ArrowRight size={14} strokeWidth={1.5} aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}

function ValueCell({
  value,
  picked,
  onPick,
  disabled,
  ariaLabel,
}: {
  value: unknown;
  picked: boolean;
  onPick: () => void;
  disabled: boolean;
  ariaLabel: string;
}) {
  // Radio-button pattern via Lucide: empty Circle when unpicked, a
  // bold filled CheckCircle2 (heavier stroke, solid navy chip via
  // .merge-review-pick.is-on) when picked — the selected side must be
  // unmistakable at a glance.
  return (
    <div className="merge-review-value-cell">
      <button
        type="button"
        className={`merge-review-pick${picked ? " is-on" : ""}`}
        onClick={onPick}
        disabled={disabled}
        aria-pressed={picked}
        aria-label={ariaLabel}
      >
        {picked ? (
          <CheckCircle2 size={19} strokeWidth={2.75} />
        ) : (
          <Circle size={16} strokeWidth={1.5} />
        )}
      </button>
      <span className="merge-review-value">{formatValue(value)}</span>
    </div>
  );
}

function SensitiveLossPanel({
  labels,
  acked,
  onAck,
}: {
  labels: string[];
  acked: boolean;
  onAck: (next: boolean) => void;
}) {
  return (
    <div className="merge-review-sensitive-panel" role="alert">
      <strong>Sensitive data on merged contact will be lost.</strong>
      <p style={{ margin: "6px 0", fontSize: 14 }}>
        The merged contact has the following sensitive values on file
        that the survivor doesn't carry across (encryption-scoped
        per-contact, not transferable):
      </p>
      <ul style={{ margin: "6px 0 10px", paddingLeft: 20, fontSize: 14 }}>
        {labels.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
      <label
        className="merge-review-sensitive-ack"
        style={{ display: "flex", gap: 8, fontSize: 14 }}
      >
        <input
          type="checkbox"
          checked={acked}
          onChange={(e) => onAck(e.target.checked)}
        />
        I acknowledge that this sensitive data will be permanently
        lost on merge. The survivor's existing values (if any) are
        unaffected.
      </label>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────

function displayName(c: SafeContact): string {
  const first = c.preferred_name ?? c.legal_first_name;
  const last = c.legal_last_name;
  const joined = [first, last].filter(Boolean).join(" ").trim();
  return joined === "" ? "(no name on file)" : joined;
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "string") return v.length === 0 ? "—" : v;
  return String(v);
}
